import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const navSections = [
  {
    label: "Général",
    items: [
      { to: "/admin", label: "Tableau de bord", icon: "ri-dashboard-line", end: true },
      { to: "/admin/indicateurs", label: "Chiffres clés", icon: "ri-bar-chart-line" },
      { to: "/admin/parametres", label: "Infos générales", icon: "ri-settings-3-line" },
    ],
  },
  {
    label: "Contenu du site",
    items: [
      { to: "/admin/missions", label: "Missions", icon: "ri-heart-3-line" },
      { to: "/admin/arguments", label: "Arguments", icon: "ri-chat-smile-3-line" },
      { to: "/admin/equipe", label: "Équipe", icon: "ri-team-line" },
      { to: "/admin/historique", label: "Historique", icon: "ri-history-line" },
      { to: "/admin/valeurs", label: "Valeurs", icon: "ri-shield-check-line" },
      { to: "/admin/mission", label: "Notre mission", icon: "ri-flag-line" },
    ],
  },
  {
    label: "Actions & dons",
    items: [
      { to: "/admin/actions", label: "Actions", icon: "ri-hand-heart-line" },
      { to: "/admin/realisations", label: "Réalisations", icon: "ri-image-line" },
      { to: "/admin/faq", label: "FAQ des dons", icon: "ri-questionnaire-line" },
      { to: "/admin/dons", label: "Réglages des dons", icon: "ri-money-euro-circle-line" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/admin/membres", label: "Membres", icon: "ri-user-settings-line" },
    ],
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { settings } = useSiteSettings();

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/auth/login");
  }

  function closeMenu() {
    setMobileOpen(false);
  }

  return (
    <div className="min-h-screen flex bg-background-50">
      {/* Voile de fond mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground-950/40 lg:hidden"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Barre supérieure mobile */}
      <header className="fixed top-0 inset-x-0 z-30 flex items-center gap-3 px-4 h-14 bg-background-50 border-b border-background-200 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Ouvrir le menu"
          className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition cursor-pointer"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
        <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
          {settings?.logo ? (
            <>
              <img
                src={settings.logo}
                alt={settings.association_name || "Logo"}
                style={{ height: "32px" }}
                className="w-auto object-contain"
              />
              <span className="font-heading text-base font-bold text-foreground-950">
                {settings.association_name || "Humanity59"}
              </span>
            </>
          ) : (
            <>
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-heart-3-fill text-base"></i>
              </span>
              <span className="font-heading text-base font-bold text-foreground-950">
                Humanity<span className="text-primary-500">59</span>
              </span>
            </>
          )}
        </Link>
      </header>

      {/* Menu latéral */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 bg-background-50 border-r border-background-200 flex flex-col transform transition-transform duration-200 ease-in-out lg:static lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-5 border-b border-background-200 flex items-center justify-between">
          <Link to="/admin" onClick={closeMenu} className="flex items-center gap-2 cursor-pointer">
            {settings?.logo ? (
              <>
                <img
                  src={settings.logo}
                  alt={settings.association_name || "Logo"}
                  style={{ height: "36px" }}
                  className="w-auto object-contain"
                />
                <span className="font-heading text-lg font-bold text-foreground-950">
                  {settings.association_name || "Humanity59"}
                </span>
              </>
            ) : (
              <>
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-heart-3-fill text-lg"></i>
                </span>
                <span className="font-heading text-lg font-bold text-foreground-950">
                  Humanity<span className="text-primary-500">59</span>
                </span>
              </>
            )}
          </Link>
          <button
            type="button"
            onClick={closeMenu}
            aria-label="Fermer le menu"
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 transition cursor-pointer lg:hidden"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {navSections.map((section) => (
            <div key={section.label}>
              <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">
                {section.label}
              </p>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                        isActive
                          ? "bg-primary-100 text-primary-700"
                          : "text-foreground-700 hover:bg-background-100"
                      }`
                    }
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-background-200 space-y-1">
          <Link
            to="/"
            onClick={closeMenu}
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition cursor-pointer whitespace-nowrap"
          >
            <i className="ri-global-line text-lg"></i>
            Voir le site
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-primary-600 hover:bg-primary-100 transition cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line text-lg"></i>
            Se déconnecter
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-background-100/40 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}